/**
 * Земля под полем (ISSUE-87): площадь с брусчаткой одной картинкой из атласа `arena` (`tools/voxel/arena`), трава
 * плиткой под всем экраном и деревья на траве за площадью. Статична: собирается один раз на размер экрана и кешируется
 * в одну текстуру (`cacheAsTexture`) — в кадре это один спрайт.
 *
 * Площадь — ромб по осям модели, как на макете. Паркан стоит среди юнитов ({@link layoutFence}): кадры `far` и `near`
 * режутся на вертикальные полосы, у каждой — глубина паркана в её столбце. Зомби за парканом рисуется под ним, перед
 * парканом — над ним, и снаружи площади, и внутри.
 */
import type { FieldGeometry, Vec2 } from "@newfungame/contracts";
import { FIXED_ONE } from "@newfungame/contracts";
import { type Container, Rectangle, Sprite, Texture, TilingSprite } from "pixi.js";
import { frameAt, type AnimResolver } from "./anim";
import type { LoadedAtlas } from "./atlas";
import { depthOf, Layer, spriteScale, worldToScreen, type Camera } from "./iso";

export interface GroundInput {
  atlas: LoadedAtlas;
  anims: AnimResolver;
  cam: Camera;
  field: FieldGeometry;
  hq: Vec2;
  width: number;
  height: number;
  seed?: number;
  /** Бета (ISSUE-163): локация уровня — своя земля и декор вместо травы и деревьев, если их кадры уже загружены. */
  location?: string;
}

/**
 * Геометрия площади в клетках поля от центра штаба — та же, что в `tools/voxel/arena/arena.py`:
 * `fence` — полудиагональ ромба паркана (`FENCE · √2`): паркан — это |fx| + |fy| = fence; `square` — непрозрачная
 * часть кадра `square` (дальше растушёвка в траву; деревья ставятся только снаружи).
 */
export const ARENA = {
  fence: 4.8 * Math.SQRT2,
  square: { x: 8.2, up: 14.2, down: 12.2 },
} as const;

/** Ширина полосы паркана в пикселях атласа (~0,35 клетки): ошибка глубины у диагонали — не больше полосы. */
const FENCE_STRIP = 24;

/** Деревья на траве: сетка в клетках и доля занятых узлов. */
const TREE_GRID = { x: 2.2, y: 3.0, chance: 0.42 };
const TREE_VARIANTS = 3;

/**
 * Локации беты (ISSUE-163): кадры атласа `arena-locs` (`tools/ui-art/locs.mjs`, грузится лениво). Земля —
 * `<loc>_ground`, декор — `<loc>_<имя>`; `chance` — доля занятых узлов сетки.
 */
export const LOCATIONS: Record<string, { props: readonly string[]; chance: number }> = {
  desert: {
    props: ["saguaro", "barrel_cactus", "sandrock", "dune", "pebbles", "deadtree", "dune", "saguaro", "skull"],
    chance: 0.36,
  },
  snow: {
    props: ["fir", "fir_wide", "fir", "snowman", "drift", "sapling", "snowrocks", "drift", "fir_wide", "sign"],
    chance: 0.4,
  },
  wasteland: {
    props: ["car", "wall", "barrels", "rubble", "tires", "lamp", "burnt", "rubble", "wall", "burnt"],
    chance: 0.34,
  },
};

/** Площадь локации без травы: ромб брусчатки с бордюром и лавки за верхним краем (кадр `square_bare`). */
export function inPlaza(fx: number, fy: number): boolean {
  return Math.abs(fx) + Math.abs(fy) < 9.3 || (fy > 8 && fy < ARENA.square.up && Math.abs(fx) < 7.2);
}

export interface TreeSpot {
  /** Экранные координаты якоря (земля под стволом). */
  x: number;
  y: number;
  variant: number;
  scale: number;
  /** Зеркально по горизонтали (локации: один предмет листа смотрится по-разному). */
  flip?: boolean;
}

/** Детерминированный ГПСЧ (LCG), чтобы земля не менялась между сборками. */
function rng(seed: number): () => number {
  let s = seed >>> 0;
  return () => {
    s = (Math.imul(s, 1664525) + 1013904223) >>> 0;
    return s / 4294967296;
  };
}

/**
 * Где стоят деревья: узлы сетки в клетках поля вокруг штаба, снаружи площади и в пределах экрана (с запасом на крону).
 * Чисто и без PixiJS — проверяется unit-тестом.
 */
export function planTrees(
  cam: Camera,
  hq: Vec2,
  width: number,
  height: number,
  seed = 7,
  opts: { variants?: number; chance?: number; inside?: (fx: number, fy: number) => boolean; flip?: boolean } = {},
): TreeSpot[] {
  const variants = opts.variants ?? TREE_VARIANTS;
  const chance = opts.chance ?? TREE_GRID.chance;
  const inside =
    opts.inside ??
    ((fx: number, fy: number) => Math.abs(fx) < ARENA.square.x && fy < ARENA.square.up && fy > -ARENA.square.down);
  const rnd = rng(seed);
  const out: TreeSpot[] = [];
  const o = worldToScreen(cam, hq.x, hq.y);
  // столько клеток помещается от штаба до края экрана (по вертикали клетка — полвысоты)
  const cellsX = Math.ceil(Math.max(o.x, width - o.x) / cam.scale) + 2;
  const cellsY = Math.ceil(Math.max(o.y, height - o.y) / (cam.scale / 2)) + 3;
  for (let gy = -cellsY; gy <= cellsY; gy += TREE_GRID.y) {
    for (let gx = -cellsX; gx <= cellsX; gx += TREE_GRID.x) {
      // три числа на узел всегда, чтобы раскладка не зависела от размера экрана
      const r = rnd();
      const jx = (rnd() - 0.5) * TREE_GRID.x * 0.8;
      const jy = (rnd() - 0.5) * TREE_GRID.y * 0.8;
      if (r > chance) continue;
      const fx = gx + jx;
      const fy = gy + jy;
      if (inside(fx, fy)) continue;
      const x = o.x + fx * cam.scale;
      const y = o.y - (fy * cam.scale) / 2;
      // крона до ~1,3 клетки в стороны и ~2,5 вверх
      if (x < -1.3 * cam.scale || x > width + 1.3 * cam.scale || y < 0 || y > height + 2.5 * cam.scale) continue;
      const variant = Math.floor(rnd() * variants);
      const sc = 0.85 + rnd() * 0.4;
      out.push(opts.flip ? { x, y, variant, scale: sc, flip: rnd() < 0.5 } : { x, y, variant, scale: sc });
    }
  }
  out.sort((a, b) => a.y - b.y);
  return out;
}

/**
 * Глубина полосы паркана со столбцом `fx` (клетки по экрану от штаба): `far` — верхний ромб, `near` — нижний. За краем
 * ромба (фонари на углах) — глубина угла.
 */
export function fenceDepth(hq: Vec2, side: "far" | "near", fx: number): number {
  const fy = Math.max(0, ARENA.fence - Math.abs(fx)) * (side === "far" ? 1 : -1);
  return depthOf(hq.y + fy * FIXED_ONE, Layer.ground);
}

function frame(g: GroundInput, name: string) {
  const a = g.anims.get("arena", name, "s");
  return a ? (g.atlas.frames.get(frameAt(a, 0)) ?? null) : null;
}

/** Земля и декор локации беты; `false` — кадров локации в атласе ещё нет (грузятся лениво), рисуется трава. */
function buildLocation(target: Container, g: GroundInput, o: Vec2): boolean {
  const loc = g.location ? LOCATIONS[g.location] : undefined;
  const ground = loc && frame(g, `${g.location}_ground`);
  const square = loc && frame(g, "square_bare");
  if (!loc || !ground || !square) return false;
  const { cam } = g;
  const t = new TilingSprite({ texture: ground.texture, width: g.width, height: g.height });
  t.tileScale.set(spriteScale(cam, 1 / ground.unit));
  t.tilePosition.set(o.x, o.y);
  target.addChild(t);
  const sp = new Sprite(square.texture);
  sp.anchor.set(square.anchorX, square.anchorY);
  sp.scale.set(spriteScale(cam, 1 / square.unit));
  sp.position.set(o.x, o.y);
  target.addChild(sp);
  const frames = loc.props.map((n) => frame(g, `${g.location}_${n}`));
  if (frames.every((f) => !f)) return true;
  const spots = planTrees(cam, g.hq, g.width, g.height, g.seed, {
    variants: frames.length,
    chance: loc.chance,
    inside: inPlaza,
    flip: true,
  });
  for (const s of spots) {
    const f = frames[s.variant];
    if (!f) continue;
    const prop = new Sprite(f.texture);
    prop.anchor.set(f.anchorX, f.anchorY);
    const k = spriteScale(cam, 1 / f.unit) * s.scale;
    prop.scale.set(s.flip ? -k : k, k);
    prop.position.set(s.x, s.y);
    target.addChild(prop);
  }
  return true;
}

/** Рисует землю; возвращает, чем: `grass` или имя локации (кадры локации могут ещё не догрузиться — тогда трава). */
export function buildGround(target: Container, g: GroundInput): string {
  for (const c of target.removeChildren()) c.destroy();
  const { cam } = g;
  const o = worldToScreen(cam, g.hq.x, g.hq.y);
  if (buildLocation(target, g, o)) return g.location!;

  const grass = frame(g, "grass");
  if (grass) {
    const k = spriteScale(cam, 1 / grass.unit);
    const t = new TilingSprite({ texture: grass.texture, width: g.width, height: g.height });
    t.tileScale.set(k);
    // плитка привязана к штабу: при смене размера экрана трава не «едет»
    t.tilePosition.set(o.x, o.y);
    target.addChild(t);
  }

  const square = frame(g, "square");
  if (square) {
    const sp = new Sprite(square.texture);
    sp.anchor.set(square.anchorX, square.anchorY);
    sp.scale.set(spriteScale(cam, 1 / square.unit));
    sp.position.set(o.x, o.y);
    target.addChild(sp);
  }

  const trees = Array.from({ length: TREE_VARIANTS }, (_, i) => frame(g, `tree_${i}`));
  if (trees.some(Boolean)) {
    for (const t of planTrees(cam, g.hq, g.width, g.height, g.seed)) {
      const f = trees[t.variant] ?? trees.find(Boolean)!;
      const sp = new Sprite(f.texture);
      sp.anchor.set(f.anchorX, f.anchorY);
      sp.scale.set(spriteScale(cam, 1 / f.unit) * t.scale);
      sp.position.set(t.x, t.y);
      target.addChild(sp);
    }
  }
  return "grass";
}

/**
 * Паркан: полосы кадров `far` и `near` прямо в контейнере юнитов (сортируются вместе с ними). Прежние полосы из `strips`
 * удаляются, новые кладутся туда же. Без кадров в атласе — паркана нет.
 */
export function layoutFence(units: Container, strips: Sprite[], g: GroundInput): void {
  for (const sp of strips.splice(0)) {
    sp.texture.destroy(false);
    sp.destroy();
  }
  const o = worldToScreen(g.cam, g.hq.x, g.hq.y);
  for (const side of ["far", "near"] as const) {
    const f = frame(g, side);
    if (!f) continue;
    const tex = f.texture;
    const { width: w, height: h } = tex.frame;
    const ax = f.anchorX * w;
    const ay = f.anchorY * h;
    const k = spriteScale(g.cam, 1 / f.unit);
    for (let x = 0; x < w; x += FENCE_STRIP) {
      const sw = Math.min(FENCE_STRIP, w - x);
      const t = new Texture({ source: tex.source, frame: new Rectangle(tex.frame.x + x, tex.frame.y, sw, h) });
      const sp = new Sprite(t);
      sp.anchor.set((ax - x) / sw, ay / h);
      sp.scale.set(k);
      sp.position.set(o.x, o.y);
      // столбец полосы в клетках поля: пиксель атласа = √2 / tilePixels клетки по горизонтали
      sp.zIndex = fenceDepth(g.hq, side, (x + sw / 2 - ax) * f.unit * Math.SQRT2);
      units.addChild(sp);
      strips.push(sp);
    }
  }
}
